/**
 * 需求来源：docs/合作页、CJ Entity、Ads开户落地页3个/AI提示词.md（第2次提交 任务1；第15次提交第13次补充：补图标+补滚动）、advertisingservice.html。
 * Section 2：cj-platform-marquee 平台 logo 跑马灯（两组相同 group 无缝循环横滚）。
 * 注意事项：
 * 1. 平台名文案经 ConstantsContext 取词（MARQUEE.logos）；logo 图片 src 为视觉资产。
 * 2. 260714 第13次补充：applovin/bing/taboola/outbrain 原用 cdn.simpleicons.org 远程 slug 不存在(404 broken)，
 *    改用本地资源 asset/images/advertisingservice/*.png（用户已放置）；meta/google/tiktok/snapchat 保留可用的 simpleicons。
 * 3. 260714 第13次补充：横滚动画改由 requestAnimationFrame 驱动。根因——本页 index.less 以 CSS Modules :global{} 包裹，
 *    css-loader 会把 `@keyframes cjPlatformScroll` 作用域化为带 hash 的 index__cjPlatformScroll___xxx，而 :global 内
 *    `animation: cjPlatformScroll` 引用保持全局名，二者不匹配 → 动画不实例化(getAnimations()=[]、transform 恒 none)，
 *    故原 CSS 跑马灯不生效。用组件内 rAF 平移 track（内联 transform 覆盖失效的 CSS animation），必然生效且页面自包含；
 *    含 hover 暂停、prefers-reduced-motion 静止、SSR 安全（仅客户端 useEffect）。
 */
import React, { useContext, useEffect, useRef } from 'react';
import ConstantsContext from './ConstantsContext';
import applovinLogo from 'asset/images/advertisingservice/applovin.png';
import bingLogo from 'asset/images/advertisingservice/bing.png';
import taboolaLogo from 'asset/images/advertisingservice/taboola.png';
import outbrainLogo from 'asset/images/advertisingservice/outbrain.png';
// 260715 第17次补充(需求㊷)：meta/google/tiktok/snapchat 原 cdn.simpleicons.org 外链改为本地 svg，不再引用外部链接
import metaLogo from 'asset/images/advertisingservice/meta.svg';
import googleLogo from 'asset/images/advertisingservice/google.svg';
import tiktokLogo from 'asset/images/advertisingservice/tiktok.svg';
import snapchatLogo from 'asset/images/advertisingservice/snapchat.svg';

// logo 图片顺序与 MARQUEE.logos 一一对应：Meta / Google / TikTok / AppLovin / Bing / Snapchat / Taboola / Outbrain
var LOGO_SRCS = [
  metaLogo,
  googleLogo,
  tiktokLogo,
  applovinLogo,
  bingLogo,
  snapchatLogo,
  taboolaLogo,
  outbrainLogo,
];

function Group({ hidden, names }) {
  return (
    <div className="cj-platform-marquee__group" aria-hidden={hidden ? 'true' : undefined}>
      {names.map((name, i) => (
        <div className="cj-platform-marquee__item" key={name}>
          <img className="cj-platform-marquee__logo" src={LOGO_SRCS[i]} alt={hidden ? '' : name} loading="lazy" />
          <span className="cj-platform-marquee__name">{name}</span>
        </div>
      ))}
    </div>
  );
}

function PlatformMarquee() {
  const { MARQUEE } = useContext(ConstantsContext);
  const names = MARQUEE.logos;
  const viewportRef = useRef(null);
  const trackRef = useRef(null);

  // 260714 第13次补充：rAF 驱动无缝横滚（详见文件头注释）
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const track = trackRef.current;
    const viewport = viewportRef.current;
    if (!track || !viewport) return undefined;
    // 尊重系统「减少动态效果」：静止不滚
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    const SPEED = 60; // px/s，与原 CSS 32s 走完一组的观感相当
    let offset = 0;
    let last = 0;
    let paused = false;
    let rafId = 0;
    // 一组(含右侧 gap)的宽度：track 由两组等宽组构成，滚过一组即可无缝复位
    const groupWidth = () => {
      const g = track.firstElementChild;
      return g ? g.getBoundingClientRect().width : track.scrollWidth / 2;
    };

    const step = (ts) => {
      if (!last) last = ts;
      const dt = (ts - last) / 1000;
      last = ts;
      if (!paused) {
        offset += SPEED * dt;
        const w = groupWidth();
        if (w > 0 && offset >= w) offset -= w; // 无缝复位
        track.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';
      }
      rafId = window.requestAnimationFrame(step);
    };
    rafId = window.requestAnimationFrame(step);

    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    viewport.addEventListener('mouseenter', onEnter);
    viewport.addEventListener('mouseleave', onLeave);

    return () => {
      window.cancelAnimationFrame(rafId);
      viewport.removeEventListener('mouseenter', onEnter);
      viewport.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <section className="cj-platform-marquee" aria-label="Supported advertising platforms">
      <div className="cj-platform-marquee__inner">
        <p className="cj-platform-marquee__label">{MARQUEE.label}</p>
        <div className="cj-platform-marquee__viewport" ref={viewportRef}>
          <div className="cj-platform-marquee__track" ref={trackRef}>
            <Group hidden={false} names={names} />
            <Group hidden names={names} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default PlatformMarquee;
